import { ref } from 'vue'
import { api } from '@/global/services/api'
import { isArray } from '@/global/utils/utils'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { buildIrregularityFilters } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/composables/workOrderData'
import { IRREGULARITIES_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/config/constants'

// The reports are paginated. One order's window holds nothing like this many, so a
// single page is the whole of it.
const ALL_IRREGULARITIES_PAGINATION = {
  currentPage: 1,
  perPage: 200
}

/**
 * The irregularities the map marks beside a work order's route.
 *
 * An irregularity carries the task and the vehicle rather than the order, so there is
 * no "irregularities of this order" endpoint to ask - the reports are narrowed by the
 * pair the order ran with, within the window it was driven in, which is exactly how
 * the backend counts them for the listing.
 *
 * Once fetched they are remembered, so marking an order off and back on again costs
 * nothing.
 */
export function useWorkOrderIrregularities () {
  const irregularitiesByOrder = ref({})
  const isFetching = ref(false)

  const fetchIrregularities = async (filters) => {
    const { data } = await api(WINTER_SERVICE_MODULE).get(IRREGULARITIES_ENDPOINT, {
      filters: JSON.stringify(filters),
      pagination: JSON.stringify(ALL_IRREGULARITIES_PAGINATION)
    })

    return isArray(data) ? data : []
  }

  /**
   * The reports of every given order, fetching only the ones not seen yet. Answers
   * with them flattened, which is what the map panel takes.
   */
  const loadIrregularities = async (records) => {
    const orders = (isArray(records) ? records : []).filter(record => !!record?.id)
    const missing = orders.filter(record => irregularitiesByOrder.value[record.id] === undefined)

    if (missing.length) {
      try {
        isFetching.value = true

        const fetched = await Promise.all(missing.map(async (record) => {
          const filters = buildIrregularityFilters(record)

          return {
            id: record.id,
            // An order without a task or without a vehicle has nothing that could
            // have been reported against it - remembered as empty so it is not
            // asked for again.
            irregularities: filters === null ? [] : await fetchIrregularities(filters)
          }
        }))

        // One assignment rather than one per order: the map redraws on every change,
        // and a whole selection is a single change.
        const collected = { ...irregularitiesByOrder.value }

        fetched.forEach(({ id, irregularities }) => {
          collected[id] = irregularities
        })

        irregularitiesByOrder.value = collected
      }
      catch (exception) {
        console.error(exception)
      }
      finally {
        isFetching.value = false
      }
    }

    return orders.flatMap(record => irregularitiesByOrder.value[record.id] || [])
  }

  return {
    isFetching,
    loadIrregularities
  }
}
